import { Box, Link, Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import React from "react";

import { AuthFormWrapper } from "components/authentication/AuthFormWrapper";
import {
  RegisterForm,
  RegisterFormState,
} from "components/authentication/create-account";
import { ConfirmEmailDialog } from "components/authentication/create-account/ConfirmEmailDialog";

import { ROUTES } from "constants/routes";
import useAuth from "hooks/useAuth";

const CreateAccount: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { register, login, user } = useAuth();
  const [awaitingVerification, setAwaitingVerification] = React.useState(false);
  const [timer, setTimer] = React.useState<number | null>(null);
  const [emailAddress, setEmailAddress] = React.useState("");

  const attemptLogin = async (email: string, password: string) => {
    try {
      await login({
        email,
        password,
      });
    } catch (error: unknown) {
      // Expected
    }
  };

  const handleSubmit = async (values: RegisterFormState) => {
    try {
      await register(values);
      setEmailAddress(values.email);
      enqueueSnackbar("Account created", { variant: "success" });
      setAwaitingVerification(true);

      const timeout = window.setInterval(() => {
        attemptLogin(values.email, values.password);
      }, 4000);

      setTimer(timeout);
    } catch (err: unknown) {
      const message = (err as Error).message.split(":");
      enqueueSnackbar(`Could not create account: ${message[1]}`, {
        variant: "error",
      });
    }
  };

  React.useEffect(() => {
    // There is an interval
    if (user && typeof timer === "number") {
      window.clearInterval(timer);
      setTimer(0);
      const { plan } = router.query;
      if (plan && typeof plan === "string") {
        router.push({
          pathname: ROUTES.REGISTER_PLAN_SELECTION,
          query: { plan },
        });
      } else {
        router.push(ROUTES.REGISTER_PLAN_SELECTION);
      }
    }
    // Already signed in, we boot to login
    else if (user && !timer) {
      router.push(ROUTES.AUTH_LOGIN);
    }
  }, [user, router, timer]);

  return (
    <AuthFormWrapper title="Create Account">
      <ConfirmEmailDialog open={awaitingVerification} email={emailAddress} />
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          mb: 3,
          backgroundColor: "primaryMain",
        }}
      >
        <div>
          <Typography color="textPrimary" gutterBottom variant="h3">
            Create an Account
          </Typography>
          <Typography color="textSecondary" variant="body1">
            {"Already have an account? "}
            <Link color="primary" component="a" href={ROUTES.AUTH_LOGIN}>
              Sign in
            </Link>
          </Typography>
        </div>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: "primaryMain",
        }}
      >
        <RegisterForm onSubmit={handleSubmit} />
      </Box>
    </AuthFormWrapper>
  );
};

export default CreateAccount;
